<template>
  <div class="py-3 px-10 fill-height">
    <v-dialog
      v-if="job"
      v-model="dialogJobApplication"
      max-width="550"
      persistent scrollable
      :fullscreen="!$vuetify.breakpoint.smAndUp"
    >
      <JobApplication @applied="applied" @close="closeDialog" :job="job" />
    </v-dialog>
    <v-dialog
      v-if="dialogCoursesInfo"
      v-model="dialogCoursesInfo"
      max-width="550"
      :fullscreen="!$vuetify.breakpoint.smAndUp"
    >
      <GetAccessPostulant @close="() => { dialogCoursesInfo = false }" />
    </v-dialog>
    <div class="fill-height" v-if="!job">
      <div
        style="width: 100%; height: 100%; position: relative"
        class="white d-flex align-center flex-column py-7"
        v-if="loading"
      >
        <v-skeleton-loader ref="skeleton" type="avatar" class="pb-4 mx-auto"></v-skeleton-loader>
        <v-skeleton-loader ref="skeleton" type="text" class="mx-auto" style="width: 100%"></v-skeleton-loader>
        <v-skeleton-loader ref="skeleton" type="text" class="mx-auto pb-4" style="width: 50%"></v-skeleton-loader>
        <v-skeleton-loader ref="skeleton" type="chip" class="mx-auto pb-8"></v-skeleton-loader>
        <v-skeleton-loader
          ref="skeleton"
          type="paragraph"
          class="mb-4"
          style="width: 100%"
          v-for="n in 4"
          :key="n"
        ></v-skeleton-loader>
      </div>
      <div v-else class="fill-height">
        <div
          v-if="!error"
          class="white d-flex align-center flex-column justify-center py-7 px-10 fill-height"
        >
          <span class="display-1 font-weight-light text-center mb-10">
            Encuentra tu
            <span class="font-weight-bold primary--text">trabajo ideal</span>
            en tan solo un click
          </span>
          <div style="width: 100%" class="mt-5">
            <v-img src="@/assets/entrevista.svg"></v-img>
          </div>
        </div>
        <div
          v-else
          class="white d-flex align-center flex-column justify-center py-7 px-10 fill-height"
        >
          <span
            class="headline font-weight-light text-center mb-10"
          >Ups. No encontramos el aviso al que estás intentando acceder.</span>
          <span class="display-3 font-weight-bold primary--text text-center">404</span>
          <div style="width: 100%">
            <v-img src="@/assets/404.svg"></v-img>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="fill-height">
      <div
        v-if="isApplied"
        class="white d-flex align-center flex-column justify-center py-7 px-10 fill-height"
      >
        <div class="d-flex">
          <v-spacer v-if="!$vuetify.breakpoint.mdAndUp"></v-spacer>
          <v-btn color="mb-4" depressed small fab @click="close()">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <h3 class="headline font-weight-light mb-2 text-center">
          Felicidades te postulaste a
          <span class="font-weight-bold primary--text">{{ job.titulo }}</span>
          en la empresa
          <span
            class="font-weight-bold primary--text"
          >{{ job.empresa.nombre_empresa }}</span>.
          ¡Buena suerte!
        </h3>
        <div style="width: 100%" class="mt-5">
          <v-img src="@/assets/apply-work.svg"></v-img>
        </div>
      </div>
      <div v-else>
        <div>
          <div class="d-flex">
            <v-spacer v-if="!$vuetify.breakpoint.mdAndUp"></v-spacer>
            <v-btn large icon @click="close()">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <div class="text-center mb-4">
            <v-avatar
              :color="!(!!job.empresa.profile_image && user) ? 'primary' : ''"
              size="128">
              <v-img
                v-if="!!job.empresa.profile_image && user"
                :src="`${host}${job.empresa.profile_image}`"
                :alt="job.empresa.nombre_empresa"
              ></v-img>
              <span v-else class="white--text display-2 font-weight-bold">{{ initials }}</span>
            </v-avatar>
          </div>
          <div class="d-flex justify-center">
            <div v-if="job.permitir_discapacitados" class="text-center mt-2 d-flex justify-center align-center">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon>mdi-human-wheelchair</v-icon>
                  </v-btn>
                </template>
                <span>Apto para personas con discapacidad</span>
              </v-tooltip>
            </div>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <div v-on="on" class="d-flex flex-column align-center justify-center ml-2">
                  <v-icon>mdi-account-group</v-icon>
                  <span class="font-weight-bold grey--text text--darken-1">{{ job.vacantes }}</span>
                </div>
              </template>
              <span>Número de vacantes disponibles</span>
            </v-tooltip>
          </div>
          <div class="headline mt-2 font-weight-bold text-center" v-text="job.titulo"></div>
          <div v-if="user" class="primary--text text-center" v-text="job.empresa.nombre_empresa"></div>
          <div class="text-center mt-4">
            <BaseMyButton
              v-if="!alreadyApplied"
              :borderRadius="100"
              @click.stop="loggedIn ? dialogJobApplication = true : dialogCoursesInfo = true"
            >Postularme</BaseMyButton>
            <BaseMyButton
              v-else
              :borderRadius="100"
              disabled
            >Postulado el {{ formatDate(alreadyApplied.createdAt) }}</BaseMyButton>
          </div>
          <div class="text-center mt-4">
            <v-chip
              v-if="job.requerimientos && job.requerimientos.salario && job.requerimientos.salario.mostrar"
              small
              color="success lighten-5"
              class="success--text"
            >
              <v-icon small>mdi-currency-usd</v-icon>{{ job.requerimientos.salario.salario_min }} - {{ job.requerimientos.salario.salario_max }}
            </v-chip>
          </div>
        </div>
        <v-divider class="my-8"></v-divider>
        <v-list-item class="pa-0">
          <v-list-item-content class="pa-0">
            <v-list-item-subtitle class="mb-2 d-flex flex-wrap">
              <span class="grey--text mr-4">Lugar de trabajo</span>
              <span
                style="white-space: break-spaces;"
                class="font-weight-bold grey--text text--darken-2"
                v-text="`${job.provincia}, ${job.ciudad}`"
              ></span>
            </v-list-item-subtitle>
            <v-list-item-subtitle class="mb-2 d-flex flex-wrap">
              <span class="grey--text mr-4">Publicado</span>
              <span
                style="white-space: break-spaces;"
                class="font-weight-bold grey--text text--darken-2"
                v-text="`Publicado ${timeElapsed}`"
              ></span>
            </v-list-item-subtitle>
            <v-list-item-subtitle class="mb-2 d-flex flex-wrap">
              <span class="grey--text mr-4">Area</span>
              <span
                class="font-weight-bold grey--text text--darken-1"
                style="white-space: break-spaces;"
                v-text="job.area"
              ></span>
            </v-list-item-subtitle>
            <v-list-item-subtitle class="mb-2 d-flex flex-wrap">
              <span class="grey--text mr-4">Subarea</span>
              <span
                style="white-space: break-spaces;"
                class="font-weight-bold grey--text text--darken-2"
                v-text="job.subarea"
              ></span>
            </v-list-item-subtitle>
            <v-list-item-subtitle class="d-flex flex-wrap">
              <span class="grey--text mr-4">Tipo de puesto</span>
              <span
                style="white-space: break-spaces;"
                class="font-weight-bold grey--text text--darken-2"
                v-text="job.tipo_trabajo"
              ></span>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider class="my-8"></v-divider>
        <div class="body-2 font-weight-bold" style="width: 100%">Descripcion</div>
        <div
          class="body-2 mt-3 mb-5 grey--text text--darken-1"
          style="width: 100%"
          v-html="job.descripcion"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
import JobApplication from "@/components/JobApplication";
import GetAccessPostulant from "@/components/GetAccessPostulant";
import { mapGetters } from "vuex";

import config from '@/config';

export default {
  props: {
    job: {
      type: Object
    },
    user: {
      type: Object
    },
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    }
  },
  components: {
    JobApplication,
    GetAccessPostulant,
  },
  data() {
    return {
      dialogCoursesInfo: false,
      dialogJobApplication: false,
      isApplied: false,
      host: config.host,
    };
  },
  watch: {
    job() {
      this.isApplied = false;
    }
  },
  computed: {
    ...mapGetters("postulants/postulant", ["loggedIn"]),
    timeElapsed() {
      return this.$moment(this.job.createdAt).fromNow();
    },
    initials() {
      const nombreEmpresa = this.job.empresa.nombre_empresa;
      const nombreSplit = nombreEmpresa.split(" ");
      if (nombreSplit.length === 1) return nombreEmpresa[0].toUpperCase();
      else
        return `${nombreEmpresa[0].toUpperCase()}${nombreEmpresa[1].toUpperCase()}`;
    },
    alreadyApplied() {
      return this.user
        ? this.job.postulantes.find(
            postulante => postulante.postulante === this.user._id
          )
        : undefined;
    }
  },
  methods: {
    close() {
      this.isApplied = false;
      this.$emit("closeJobDetail");
    },
    closeDialog() {
      this.dialogJobApplication = false;
    },
    formatDate(date) {
      return this.$moment(date).format("DD/MM/YYYY");
    },
    applied() {
      this.isApplied = true;
    }
  }
};
</script>

<style>
</style>